import React, { useMemo } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { mix, shade, hexToRgba } from "../utils/color";

/**
 * Visuel de couverture d'un article.
 *
 * - Avec `image` : la photo est incrustée dans la DA du site — dégradé de la couleur de catégorie en
 *   surimpression (mode « multiply »), motif de points, et au survol : zoom lent, voile qui s'éclaircit,
 *   reflet incliné (-12°, comme le HighlightedText) qui balaie l'image.
 * - Sans `image` : illustration SVG générée de façon déterministe à partir de `seed` (slug) et de la couleur.
 *
 * Les effets de survol se déclenchent au survol du visuel lui-même ou de son parent portant la classe `group`
 * (les cartes d'articles).
 */

const Frame = tw.div`relative w-full h-full overflow-hidden bg-gray-200`;

const Photo = styled.img`
  ${tw`absolute inset-0 w-full h-full object-cover`}
  transform: scale(1.001);
  transition: transform 1200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  .group:hover &,
  ${Frame}:hover & {
    transform: scale(1.06);
  }
`;

const Tint = styled.div(({ $color }) => [
  tw`absolute inset-0 pointer-events-none`,
  `background: linear-gradient(135deg, ${hexToRgba(mix($color, "#ffffff", 0.1), 0.8)} 0%, ${hexToRgba(shade($color, -0.45), 0.9)} 100%);`,
  `mix-blend-mode: multiply; opacity: 0.72; transition: opacity 700ms ease;`,
  `.group:hover &, ${Frame}:hover & { opacity: 0.45; }`,
]);

// Léger voile clair pour garder de la luminosité et de la lisibilité malgré le multiply.
const Glow = styled.div(({ $color }) => [
  tw`absolute inset-0 pointer-events-none`,
  `background: radial-gradient(120% 90% at 15% 10%, ${hexToRgba("#ffffff", 0.22)} 0%, ${hexToRgba("#ffffff", 0)} 60%);`,
]);

const Dots = styled.div`
  ${tw`absolute inset-0 pointer-events-none`}
  background-image: radial-gradient(rgba(255, 255, 255, 0.26) 1.4px, transparent 1.7px);
  background-size: 26px 26px;
  opacity: 0.6;
  transition: opacity 700ms ease;
  .group:hover &,
  ${Frame}:hover & {
    opacity: 0.25;
  }
`;

const Shine = styled.div`
  ${tw`absolute inset-0 pointer-events-none`}
  background: linear-gradient(105deg, rgba(255, 255, 255, 0) 38%, rgba(255, 255, 255, 0.28) 50%, rgba(255, 255, 255, 0) 62%);
  transform: translateX(-130%) skewX(-12deg);
  transition: transform 1100ms cubic-bezier(0.2, 0.8, 0.2, 1);
  .group:hover &,
  ${Frame}:hover & {
    transform: translateX(130%) skewX(-12deg);
  }
`;

const Svg = styled.svg`
  ${tw`absolute inset-0 w-full h-full`}
  display: block;
  transition: transform 1200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  .group:hover &,
  ${Frame}:hover & {
    transform: scale(1.05);
  }
`;

// FNV-1a 32 bits : rapide, stable, suffisant pour dériver des positions.
const hash = str => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Générateur pseudo-aléatoire déterministe (mulberry32).
const rng = seed => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const W = 800;
const H = 450;

const buildScene = (seed, color) => {
  const h = hash(seed || "ia-besancon");
  const rand = rng(h);
  const id = `c${h.toString(36)}`;
  const from = mix(color, "#ffffff", 0.08);
  const to = shade(color, -0.42);
  const accent = mix(color, "#1a202c", 0.35);

  const circles = Array.from({ length: 3 }, (_, i) => ({
    cx: Math.round(rand() * W),
    cy: Math.round(rand() * H),
    r: Math.round(120 + rand() * 180),
    o: 0.07 + rand() * 0.08,
    fill: i === 1 ? accent : "#ffffff",
  }));

  // Un large parallélogramme incliné (-12°), clin d'œil au HighlightedText du site.
  const band = {
    x: Math.round(rand() * W * 0.5),
    y: Math.round(H * (0.35 + rand() * 0.35)),
    w: Math.round(W * (0.45 + rand() * 0.4)),
    h: Math.round(70 + rand() * 60),
  };
  const ring = { cx: Math.round(W * (0.55 + rand() * 0.4)), cy: Math.round(H * rand()), r: Math.round(60 + rand() * 90) };

  return { id, from, to, circles, band, ring, angle: Math.round(rand() * 360) };
};

/**
 * @param {boolean} [plain] Visuel déjà composé (logo, infographie, capture) : on n'applique ni teinte
 *                          de catégorie ni motif de points, qui en dénatureraient les couleurs.
 *                          Le zoom et le reflet au survol restent actifs.
 */
export default function CoverArt({ seed = "", color = "#6415FF", image, alt = "", className, eager = false, plain = false }) {
  const scene = useMemo(() => buildScene(seed, color), [seed, color]);

  if (image) {
    return (
      <Frame className={className}>
        <Photo src={image} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" />
        {!plain && (
          <>
            <Tint $color={color} aria-hidden="true" />
            <Glow $color={color} aria-hidden="true" />
            <Dots aria-hidden="true" />
          </>
        )}
        <Shine aria-hidden="true" />
      </Frame>
    );
  }

  const { id, from, to, circles, band, ring, angle } = scene;
  return (
    <Frame className={className}>
      <Svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : "true"}
        focusable="false"
      >
        <defs>
          <linearGradient id={`${id}-g`} gradientTransform={`rotate(${angle} 0.5 0.5)`}>
            <stop offset="0" stopColor={from} />
            <stop offset="1" stopColor={to} />
          </linearGradient>
          <pattern id={`${id}-d`} width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="3" r="1.7" fill="#ffffff" fillOpacity="0.16" />
          </pattern>
          <radialGradient id={`${id}-r`}>
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.22" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width={W} height={H} fill={`url(#${id}-g)`} />
        <rect width={W} height={H} fill={`url(#${id}-d)`} />
        {circles.map((c, i) => (
          <circle key={i} cx={c.cx} cy={c.cy} r={c.r} fill={c.fill} fillOpacity={c.o} />
        ))}
        <circle cx={ring.cx} cy={ring.cy} r={ring.r} fill="none" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="6" />
        <rect x={band.x} y={band.y} width={band.w} height={band.h} fill="#ffffff" fillOpacity="0.12" transform="skewX(-12)" />
        <circle cx={W * 0.2} cy={H * 0.15} r={260} fill={`url(#${id}-r)`} />
      </Svg>
      <Shine aria-hidden="true" />
    </Frame>
  );
}
